import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { subscriptionService } from '../services/subscriptionService';
import { useNotification } from '../hooks/useNotification';
import Button from '../components/common/Button';
import { getPackageName, formatDate } from '../utils/formatters';

const SubscriptionExpiredPage = () => {
  const { user, logout } = useAuth();
  const { showError } = useNotification();
  const [loading, setLoading] = useState(false);

  const handleRenew = async () => {
    setLoading(true);
    try {
      const response = await subscriptionService.renew(user?.subscription?.package);

      // Redirect to payment URL
      if (response.data.paymentUrl) {
        window.location.href = response.data.paymentUrl;
      }
    } catch (error) {
      showError(error.message || 'Failed to initiate renewal');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-primary-600 mb-2">Bahbeta</h1>
        </div>

        {/* Expired Card */}
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Subscription Expired
          </h2>

          <p className="text-gray-600 mb-6">
            To view your debtor details, kindly renew your subscription.
          </p>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
            <p className="text-sm text-blue-800 mb-2">
              <strong>Your data is safe!</strong>
            </p>
            <p className="text-sm text-blue-800">
              All your debtor information and transaction history will be restored immediately upon renewal.
            </p>
          </div>

          {/* Subscription Details */}
          <div className="border-t border-gray-200 pt-4 mb-6">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="text-left">
                <p className="text-gray-600">Package</p>
                <p className="font-medium text-gray-900">
                  {getPackageName(user?.subscription?.package)}
                </p>
              </div>
              <div className="text-right">
                <p className="text-gray-600">Expired On</p>
                <p className="font-medium text-gray-900">
                  {user?.subscription?.expiryDate ? formatDate(user.subscription.expiryDate) : 'N/A'}
                </p>
              </div>
            </div>

            <div className="mt-4">
              <p className="text-sm text-gray-600 mb-1">Price</p>
              <p className="text-2xl font-bold text-primary-600">
                {user?.subscription?.package === 'base' ? '5' : '10'} BHD
                <span className="text-sm text-gray-600 font-normal">/month</span>
              </p>
            </div>
          </div>

          {/* Renew Button */}
          <Button
            onClick={handleRenew}
            loading={loading}
            fullWidth
            size="lg"
            className="mb-4"
          >
            Click Here to Renew
          </Button>

          <button
            onClick={logout}
            className="text-sm text-gray-600 hover:text-gray-900"
          >
            Logout
          </button>
        </div>

        {/* Help Text */}
        <p className="text-center text-sm text-gray-600 mt-6">
          Need help? Contact support
        </p>
      </div>
    </div>
  );
};

export default SubscriptionExpiredPage;
